import { visiblePageNumbers } from './pagination';

interface PageControlsProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: ((pageSize: number) => void) | undefined;
  pageSizeOptions?: readonly number[] | undefined;
  itemLabel?: string;
}

export function PageControls({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  itemLabel = 'rows',
}: PageControlsProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(Math.max(page, 1), pageCount);
  const start = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, total);
  const pages = visiblePageNumbers(currentPage, pageCount);

  return (
    <nav className="page-controls" aria-label="Pagination">
      <div className="page-summary">
        <span className="page-range" aria-live="polite">
          {start.toLocaleString()}–{end.toLocaleString()} of {total.toLocaleString()} {itemLabel}
        </span>
        {onPageSizeChange && pageSizeOptions ? (
          <label className="page-size-control">
            <span>Rows per page</span>
            <select
              aria-label="Rows per page"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
      <div className="page-number-list">
        <button
          type="button"
          className="page-step"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label="Previous page"
          title="Previous page"
        >
          ‹
        </button>
        {pages.map((pageNumber, index) =>
          pageNumber === 'ellipsis' ? (
            <span className="page-ellipsis" aria-hidden="true" key={`ellipsis-${index}`}>
              …
            </span>
          ) : (
            <button
              type="button"
              className="page-number"
              aria-current={pageNumber === currentPage ? 'page' : undefined}
              aria-label={`Page ${pageNumber.toLocaleString()}`}
              onClick={() => onPageChange(pageNumber)}
              key={pageNumber}
            >
              {pageNumber.toLocaleString()}
            </button>
          ),
        )}
        <button
          type="button"
          className="page-step"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= pageCount}
          aria-label="Next page"
          title="Next page"
        >
          ›
        </button>
      </div>
    </nav>
  );
}
